import React, { useState } from "react";
import { classNames } from "../../../../../helpers/classNames";
import { TrashIcon, PlusIcon } from "@heroicons/react/solid";
import SmartTableFiltersRow from "./SmartTable___Filters___Row";

const SmartTableFilterGroup = ({
    filterGroup,
    onUpdate,
    onDelete,
    columns,
    advisors,
    level = 0,
    showDelete = true
}) => {
    const [operator, setOperator] = useState(filterGroup.operator || 'AND');

    const handleOperatorChange = (newOperator) => {
        setOperator(newOperator);
        onUpdate({
            ...filterGroup,
            operator: newOperator
        });
    };

    const handleConditionUpdate = (index, updatedCondition) => {
        const newConditions = [...filterGroup.conditions];
        newConditions[index] = updatedCondition;
        onUpdate({
            ...filterGroup,
            conditions: newConditions
        });
    };

    const handleConditionDelete = (index) => {
        const newConditions = filterGroup.conditions.filter((_, i) => i !== index);
        onUpdate({
            ...filterGroup,
            conditions: newConditions
        });
    };

    const handleAddCondition = () => {
        if (!columns || columns.length === 0) return;
        const firstFilterableColumn = columns.find(col => !["button", "copy_to_clipboard"].includes(col.col_type));
        if (!firstFilterableColumn) return;

        const newCondition = {
            type: 'condition',
            col_id: firstFilterableColumn.col_id,
            operator: "is not empty",
            col_type: firstFilterableColumn.col_type,
            comparison: null,
            options: firstFilterableColumn.options || null,
        };

        onUpdate({
            ...filterGroup,
            conditions: [...filterGroup.conditions, newCondition]
        });
    };

    const handleAddGroup = () => {
        const newGroup = {
            type: 'group',
            operator: 'AND',
            conditions: []
        };

        onUpdate({
            ...filterGroup,
            conditions: [...filterGroup.conditions, newGroup]
        });
    };

    const getIndentClass = () => {
        const indentLevels = [
            "", // level 0
            "ml-2", // level 1 - reduced from ml-4
            "ml-4", // level 2 - reduced from ml-8
            "ml-6", // level 3 - reduced from ml-12
        ];
        return indentLevels[Math.min(level, indentLevels.length - 1)] || "ml-8";
    };

    const getGroupBorderColor = () => {
        const borderColors = [
            "border-teal-300", // level 0 - more prominent
            "border-teal-200", // level 1
            "border-emerald-200", // level 2
            "border-cyan-200", // level 3
        ];
        return borderColors[Math.min(level, borderColors.length - 1)] || "border-gray-200";
    };

    const getGroupBackgroundColor = () => {
        const bgColors = [
            "bg-teal-100", // level 0 - more prominent
            "bg-teal-50", // level 1
            "bg-emerald-50", // level 2
            "bg-cyan-50", // level 3
        ];
        return bgColors[Math.min(level, bgColors.length - 1)] || "bg-gray-50";
    };

    return (
        <div className={classNames("border rounded-lg p-2", getIndentClass(), getGroupBorderColor(), getGroupBackgroundColor())}>
            {/* Group Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">
                        {level === 0 ? 'Conditional Group' : `Nested Conditional Group (Level ${level})`}
                    </span>

                    {filterGroup.conditions.length > 1 && (
                        <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">Apply</span>
                            <select
                                value={operator}
                                onChange={(e) => handleOperatorChange(e.target.value)}
                                className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="AND">ALL</option>
                                <option value="OR">ANY</option>
                            </select>
                            <span className="text-xs text-gray-500">of the following:</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center space-x-1">
                    {level < 3 && (
                        <button
                            onClick={handleAddGroup}
                            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-md transition-colors duration-150"
                            title="Add nested conditional group"
                        >
                            <div className="flex items-center space-x-1 text-xs">
                                <PlusIcon className="w-3 h-3" />
                                <span>Conditional Group</span>
                            </div>
                        </button>
                    )}

                    <button
                        onClick={handleAddCondition}
                        className="p-1 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded-md transition-colors duration-150"
                        title="Add condition"
                    >
                        <div className="flex items-center space-x-1 text-xs">
                            <PlusIcon className="w-3 h-3" />
                            <span>Condition</span>
                        </div>
                    </button>

                    {showDelete && (
                        <button
                            onClick={onDelete}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors duration-150"
                            title="Delete conditional group"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Conditions */}
            <div className="space-y-1">
                {filterGroup.conditions?.map((condition, index) => (
                    <div key={`${condition.col_id || 'group'}-${index}`}>
                        {condition.type === 'condition' ? (
                            <SmartTableFiltersRow
                                filters={{
                                    array: filterGroup.conditions,
                                    update: (idx, newCondition) => handleConditionUpdate(idx, newCondition),
                                    remove: (idx) => handleConditionDelete(idx)
                                }}
                                advisors={advisors}
                                columns={columns}
                                filter={condition}
                                thisFilterIndex={index}
                                isInGroup={true}
                                groupOperator={operator}
                            />
                        ) : (
                            <SmartTableFilterGroup
                                filterGroup={condition}
                                onUpdate={(updatedGroup) => handleConditionUpdate(index, updatedGroup)}
                                onDelete={() => handleConditionDelete(index)}
                                columns={columns}
                                advisors={advisors}
                                level={level + 1}
                                showDelete={true}
                            />
                        )}
                    </div>
                ))}
            </div>

            {filterGroup.conditions.length === 0 && (
                <div className="text-center py-3 text-gray-500 text-sm">
                    No conditions in this conditional group. Click + Condition to add filters.
                </div>
            )}
        </div>
    );
};

export default SmartTableFilterGroup; 